import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import {
  getResolvedApiToken,
  getResolvedTenantId,
  NAVIGATION_REFRESH_EVENT,
  TENANT_CREDENTIALS_EVENT,
} from '../config/api-config';
import { onTabVisibleRefresh } from '../utils/tabVisibilityRefresh';
import type { ModuleConfig } from '../types/config';
import { DEV_API_EXPLORER_MODULE, SUPER_ADMIN_MODULE } from '../config/platformModules';
import { showDevHints } from '../utils/env';
import { buildNavigationModules } from '../utils/buildNavigationModules';
import { mergeCatalogWithNavDefaults } from '../config/platformModuleNavDefaults';
import { extractTenantModuleSubscriptions } from '../utils/moduleAccess';
import { useTenantCurrency } from '../context/TenantCurrencyContext';
import { isModuleHiddenByResidenceMode } from '../utils/studentResidenceMode';
import type { PlatformModuleCatalogEntry } from '../services/api/types/modules';

/**
 * Tenant navigation modules from DB: `platform_modules` catalog + tenant subscriptions.
 * Role screen access is applied later via `canUserOpenModule` (module_access from login).
 */
export function useNavigationModules(): { modules: ModuleConfig[]; loading: boolean } {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { tenant } = useTenantCurrency();
  const residenceMode = tenant?.student_residence_mode;
  const [catalogModules, setCatalogModules] = useState<ModuleConfig[]>([]);
  const [enabledCodes, setEnabledCodes] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated || !user) {
      setCatalogModules([]);
      setEnabledCodes(null);
      setLoading(false);
      return;
    }

    if (user.role === 'super_admin') {
      setCatalogModules([SUPER_ADMIN_MODULE]);
      setEnabledCodes(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let hasLoadedOnce = false;
    let fetchId = 0;

    async function loadNavigation(background = false) {
      const id = ++fetchId;
      if (!background) setLoading(true);
      const token = getResolvedApiToken().trim();
      const tenant = getResolvedTenantId().trim();
      let codes: Set<string> | null = null;
      let modules: ModuleConfig[] = [];
      let success = false;

      if (token && /^\d+$/.test(tenant)) {
        try {
          const catalogRes = await apiService.getPlatformModuleCatalog();
          let catalog: PlatformModuleCatalogEntry[] = catalogRes.data?.modules ?? [];
          try {
            const subsRes = await apiService.listTenantModuleSubscriptions();
            codes = new Set(
              extractTenantModuleSubscriptions(subsRes.data)
                .filter((subscription) => subscription.enabled)
                .map((subscription) => subscription.module_code)
            );
            if (codes.has('teachers') || codes.has('teacher')) {
              codes.add('staff');
            }
          } catch (subsErr) {
            // Subscriptions list used to require subscription:read (admin-only).
            // If it fails, keep the catalog and let role module_access gate screens.
            console.warn('Tenant module subscriptions unavailable; showing catalog modules:', subsErr);
            codes = null;
          }
          // Include Super Admin–enabled modules that exist in MODULE_CODES but still
          // lack route_path in DB catalog (omitted by GET /modules/catalog).
          catalog = mergeCatalogWithNavDefaults(catalog, codes);
          modules = buildNavigationModules(catalog);
          success = true;
        } catch (err) {
          console.error('Failed to load DB navigation catalog:', err);
        }
      }

      if (cancelled || id !== fetchId) return;

      if (success || !background) {
        setEnabledCodes(codes);
        setCatalogModules(modules);
      }
      setLoading(false);
      if (success) hasLoadedOnce = true;
    }

    void loadNavigation(false);
    const refreshNavigation = () => void loadNavigation(hasLoadedOnce);
    window.addEventListener(TENANT_CREDENTIALS_EVENT, refreshNavigation);
    window.addEventListener(NAVIGATION_REFRESH_EVENT, refreshNavigation);
    const stopTabRefresh = onTabVisibleRefresh(refreshNavigation);

    return () => {
      cancelled = true;
      window.removeEventListener(TENANT_CREDENTIALS_EVENT, refreshNavigation);
      window.removeEventListener(NAVIGATION_REFRESH_EVENT, refreshNavigation);
      stopTabRefresh();
    };
  }, [authLoading, isAuthenticated, user?.id, user?.role]);

  const modules = useMemo(() => {
    if (user?.role === 'super_admin') {
      return [SUPER_ADMIN_MODULE];
    }

    let list = catalogModules;
    if (enabledCodes) {
      list = list.filter((module) => {
        const code = module.settings?.moduleCode;
        return typeof code === 'string' && enabledCodes.has(code);
      });
    }

    list = list.filter((module) => {
      const code =
        typeof module.settings?.moduleCode === 'string'
          ? module.settings.moduleCode
          : module.id;
      return !isModuleHiddenByResidenceMode(code, residenceMode);
    });

    if (showDevHints) {
      return [...list, DEV_API_EXPLORER_MODULE];
    }
    return list;
  }, [catalogModules, enabledCodes, user?.role, residenceMode]);

  return { modules, loading };
}
